import type { FC, ReactNode } from 'react'
import type { ShaderFrame, ShaderPointer } from '@/components/webgl/shader-canvas'

import { useCallback, useRef } from 'react'
import { clamp } from '@/lib/motion'
import ShaderCanvas, { SHADER_HEADER } from '@/components/webgl/shader-canvas'

interface WaterRippleProps {
    image: string
    className?: string
}

const RIPPLES = 20

const FRAGMENT = `${SHADER_HEADER}
uniform vec4 uRipples[${RIPPLES}];

void main() {
    vec2 uv = vUv;
    vec2 aspect = vec2(uResolution.x / uResolution.y, 1.0);
    vec2 swell = vec2(sin(uv.y * 7.0 + uTime * 0.55), cos(uv.x * 6.0 + uTime * 0.45)) * 0.0022;

    float height = 0.0;
    vec2 slope = vec2(0.0);

    for (int i = 0; i < ${RIPPLES}; i++) {
        vec4 ripple = uRipples[i];
        float age = uTime - ripple.z;

        if (ripple.w <= 0.0 || age < 0.0 || age > 3.4) continue;

        vec2 delta = (uv - ripple.xy) * aspect;
        float dist = length(delta);
        float front = dist - age * 0.42;
        float envelope = exp(-front * front * 90.0) * exp(-age * 1.3) * ripple.w;
        float phase = front * 58.0;

        height += sin(phase) * envelope;
        slope += (delta / max(dist, 0.0001)) * cos(phase) * envelope;
    }

    vec2 distorted = uv + swell + slope * 0.016;
    vec3 color = texture2D(uTexture, coverUv(distorted)).rgb;
    float crest = max(height, 0.0);

    color += crest * crest * 0.24 + height * 0.04;

    vec2 edge = vUv - 0.5;
    color *= 1.0 - dot(edge, edge) * 0.6;

    gl_FragColor = vec4(color, 1.0);
}
`

/** "Touch the water": pointer movement drops rings that ripple through the photograph. */
const WaterRipple: FC<WaterRippleProps> = ({ image, className }): ReactNode => {
    const ripples = useRef(new Float32Array(RIPPLES * 4))
    const cursor = useRef(0)
    const last = useRef({ x: -1, y: -1, time: -10 })
    const idleAt = useRef(0)

    const drop = (x: number, y: number, time: number, strength: number): void => {
        const offset = (cursor.current % RIPPLES) * 4

        ripples.current[offset] = x
        ripples.current[offset + 1] = y
        ripples.current[offset + 2] = time
        ripples.current[offset + 3] = strength
        cursor.current += 1
    }

    const onPointer = useCallback((pointer: ShaderPointer): void => {
        const distance = Math.hypot(pointer.x - last.current.x, (pointer.y - last.current.y) * 0.6)

        idleAt.current = pointer.time

        if (pointer.kind === 'down') {
            drop(pointer.x, pointer.y, pointer.time, 1.25)
            last.current = { x: pointer.x, y: pointer.y, time: pointer.time }

            return
        }

        if (distance < 0.035 && pointer.time - last.current.time < 0.5) return

        drop(pointer.x, pointer.y, pointer.time, clamp(distance * 14, 0.35, 1))
        last.current = { x: pointer.x, y: pointer.y, time: pointer.time }
    }, [])

    const onFrame = useCallback((frame: ShaderFrame): void => {
        if (frame.time - idleAt.current > 2.6 && frame.time - last.current.time > 2.1) {
            const x = 0.15 + Math.random() * 0.7
            const y = 0.35 + Math.random() * 0.5

            drop(x, y, frame.time, 0.75)
            last.current = { x, y, time: frame.time }
        }

        frame.gl.uniform4fv(frame.uniform('uRipples[0]'), ripples.current)
    }, [])

    return (
        <ShaderCanvas
            fragment={FRAGMENT}
            image={image}
            className={className}
            onPointer={onPointer}
            onFrame={onFrame}
        />
    )
}

export default WaterRipple
